/// <amd-module name="VSS/Platform/PlatformRestErrorHandler" />

import { IVssPageContext, RestErrorHandlerResponse, Services, VssService } from "./Context";
import { IVssContributionService } from "./Contribution";
import { isFeatureFlagEnabled } from "./Feature";
import { IPlatformRestErrorHandlerService, IRestErrorHandlerService } from "./IPlatformRestErrorHandler";

/**
 * If error handling fails, we won't attempt to handle another error for this many seconds.
 */
const ErrorHandlingCooldown = 10;

class PlatformRestErrorHandlerService extends VssService implements IPlatformRestErrorHandlerService {
    /**
     * If we're handling an error for any rest client, we don't want to try to handle errors for other rest clients.
     * This prevents infinite loops.
     */
    private _handlingError = false;
    /**
     * The time that error handling last failed. If error handling fails, we won't attempt to handle errors for a while.
     */
    private _errorHandlingLastFailed = 0;
    /**
     * Promise to await before making a request
     */
    private _requestBlocker = Promise.resolve();
    /**
     * value of feature flag VisualStudio.Service.WebPlatform.ContributedRestErrorHandlerQueueing
     */
    private _queueingEnabled: boolean;
    /**
     * Value of feature flag VisualStudio.Service.WebPlatform.ContributedRestErrorHandlers
     */
    private _errorHandlingEnabled: boolean;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);
        this._queueingEnabled = isFeatureFlagEnabled(pageContext, "VisualStudio.Service.WebPlatform.ContributedRestErrorHandlerQueueing", false);
        this._errorHandlingEnabled = isFeatureFlagEnabled(pageContext, "VisualStudio.Service.WebPlatform.ContributedRestErrorHandlers", false);
    }

    public requestsEnabled(): Promise<void> {
        if (this._queueingEnabled) {
            return this._requestBlocker;
        } else {
            return Promise.resolve();
        }
    }

    public async handleError(
        tries: number,
        text: string | undefined,
        response: Response | XMLHttpRequest | undefined,
        error: Error
    ): Promise<RestErrorHandlerResponse> {
        if (!this._errorHandlingEnabled || Date.now() < this._errorHandlingLastFailed + ErrorHandlingCooldown * 1000) {
            return RestErrorHandlerResponse.Continue;
        }

        if (this._handlingError) {
            if (this._queueingEnabled) {
                // if we're already handling an error, wait for that to complete and try again
                await this.requestsEnabled();
                return RestErrorHandlerResponse.Retry;
            } else {
                return RestErrorHandlerResponse.Continue;
            }
        }

        let resumeRequests: (() => void) | undefined;
        try {
            this._handlingError = true;
            this._requestBlocker = new Promise(resolve => (resumeRequests = resolve));
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            const serviceContributions = contributionService?.getContributionChildren("ms.vss-web.rest-error-handler-root");
            if (serviceContributions) {
                for (const contribution of serviceContributions) {
                    const service = contributionService.getServiceSync<IRestErrorHandlerService>(contribution);
                    if (service) {
                        let handlerResponse: RestErrorHandlerResponse;
                        if (response instanceof XMLHttpRequest) {
                            handlerResponse = await service.handleOldPlatError(tries, text, response, error);
                        } else {
                            handlerResponse = await service.handleError(tries, text, response, error);
                        }
                        if (handlerResponse === RestErrorHandlerResponse.Retry) {
                            return RestErrorHandlerResponse.Retry;
                        }
                    }
                }
            }
        } catch (innerErr) {
            // if we fail handling the error just give up
            // TODO: try to report?
            (error as any).restErrorHandlerServiceError = innerErr;
        } finally {
            this._handlingError = false;
            resumeRequests && resumeRequests();
        }
        this._errorHandlingLastFailed = Date.now();
        return RestErrorHandlerResponse.Continue;
    }
}

Services.add("IPlatformRestErrorHandlerService", { serviceFactory: PlatformRestErrorHandlerService });
